import React from "react"
import Link from "components/Link"
import Container from "components/ui/Container"
import Heading from "components/ui/Heading"

const DEPLOYMENT_MODELS = [
  {
    icon: "/images/content/nav-preset-cloud-icon.svg",
    name: "Preset Cloud",
    path: "/preset-cloud/",
    badge: "Fully Managed",
    badgeColor: "bg-lush text-white",
    description:
      "A fully managed, cloud-hosted service for Apache Superset™. Zero infrastructure overhead — Preset handles everything.",
  },
  {
    icon: "/images/content/nav-mpc-icon.svg",
    name: "Managed Private Cloud",
    path: "/preset-managed-private-cloud/",
    badge: "Managed in Your VPC",
    badgeColor: "bg-ocean text-white",
    description:
      "Preset runs inside your own AWS, GCP, or Azure account. Your data never leaves your environment — we manage the rest.",
  },
  {
    icon: "/images/content/nav-pop-icon.png",
    name: "Preset On-Prem",
    path: "/preset-on-prem/",
    badge: "Self-Operated",
    badgeColor: "bg-cove text-white",
    description:
      "Enterprise-grade Superset with Preset-proprietary capabilities, deployed on your infrastructure — cloud, hybrid, or fully air-gapped.",
  },
  {
    icon: "/images/content/Preset_Certified_Superset.svg",
    name: "Preset Certified Superset",
    path: "/certified-superset/",
    badge: "Self-Operated",
    badgeColor: "bg-cove text-white",
    description:
      "A hardened, QA-approved distribution of Apache Superset with professional support. Full infrastructure sovereignty, open-source foundation.",
  },
]

const CAPABILITIES = [
  {
    icon: "/images/content/nav-embedded-icon.svg",
    name: "Embedded Dashboards",
    path: "/embedded-dashboards/",
    description:
      "Embed interactive analytics directly inside your product. Available across all deployment models.",
  },
  {
    icon: "/images/content/nav-api-icon.svg",
    name: "Preset API",
    path: "/api/",
    description:
      "Manage workspaces, users, and assets as code. Automate and integrate Preset into any workflow.",
  },
]

function ProductCard({ icon, name, path, badge, badgeColor, description }) {
  return (
    <Link
      href={path}
      className="group flex flex-col rounded-2xl border border-black-squeeze bg-white p-6 transition-shadow hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <img
          src={icon}
          alt={`${name} icon`}
          className="h-10 w-10 flex-shrink-0"
        />
        {badge && (
          <span
            className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${badgeColor}`}
          >
            {badge}
          </span>
        )}
      </div>
      <h3 className="mt-4 text-xl font-bold text-cove group-hover:text-lush transition-colors">
        {name}
      </h3>
      <p className="mt-2 flex-1 text-base text-dark-grey">{description}</p>
      <span className="mt-4 text-sm font-bold text-lush">
        Learn more &rarr;
      </span>
    </Link>
  )
}

function CapabilityCard({ icon, name, path, description }) {
  return (
    <Link
      href={path}
      className="group flex items-start gap-5 rounded-2xl border border-black-squeeze bg-white p-6 transition-shadow hover:shadow-md"
    >
      <img
        src={icon}
        alt={`${name} icon`}
        className="mt-1 h-10 w-10 flex-shrink-0"
      />
      <div>
        <h3 className="text-xl font-bold text-cove group-hover:text-lush transition-colors">
          {name}
        </h3>
        <p className="mt-2 text-base text-dark-grey">{description}</p>
        <span className="mt-3 block text-sm font-bold text-lush">
          Learn more &rarr;
        </span>
      </div>
    </Link>
  )
}

function ProductGridSection() {
  return (
    <Container section className="py-16">
      <Heading
        level="h2"
        label="Deployment Models"
        title="Choose how you run Preset"
        subtitle="From fully managed SaaS to completely self-operated — pick the model that fits your security, compliance, and operational requirements."
        decoration="line"
      />

      <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {DEPLOYMENT_MODELS.map((product) => (
          <ProductCard key={product.name} {...product} />
        ))}
      </div>

      <div className="mt-16">
        <Heading
          level="h2"
          label="Platform Capabilities"
          title="Extend across any deployment"
          subtitle="These capabilities are available on top of your chosen deployment model."
          decoration="line"
        />
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {CAPABILITIES.map((cap) => (
            <CapabilityCard key={cap.name} {...cap} />
          ))}
        </div>
      </div>
    </Container>
  )
}

export default ProductGridSection
